import { FormattedMessage, useIntl } from 'react-intl';
import { type ReactNode } from 'react';
import cx from 'clsx';
import { useMoneyFormatter } from '@repo/design-system-kit';
import { type BasketItemProps } from '../types';
import { Quantity } from './quantity';
import styles from './basket-item.module.css';

export function BasketItem({
  item,
  itemTotal,
  currency,
  isPreview = false,
}: BasketItemProps): ReactNode {
  const { formatNumber, formatMessage } = useIntl();
  const { formatMoney } = useMoneyFormatter();
  const { title, description, measureUnit, vatRate, quantity, unitPrice } = item;

  const formatPercentage = (value: string): string => {
    return formatNumber(parseFloat(value) / 100, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
  };

  const formatCurrency = (value: number): string => {
    return formatMoney({
      amount: value,
      currency,
    });
  };

  const formatVatRate = (value: string): string => {
    return value === '' ? value : formatPercentage(value);
  };

  const getQuantityHeader = (): string =>
    `${formatMessage({ id: 'payment-link.itemisation.header.quantity' })}: `;

  return (
    <tr className={cx(styles['item-row'], isPreview && styles.preview)} data-test-basket-item>
      <td className={styles['item-cell']} data-test-basket-item-title>
        <p className={cx(styles['item-title'], 'body-1')}>{title}</p>
        {description ? <p className={styles['item-description']}>{description}</p> : null}
      </td>
      <td className={cx(styles['quantity-cell'], 'body-2')} data-test-basket-item-quantity>
        <p>
          <Quantity quantity={quantity} unit={measureUnit} />
        </p>
      </td>
      <td className={cx(styles['vat-cell'], 'body-2')} data-test-basket-item-vat>
        <p>{formatVatRate(vatRate)}</p>
      </td>
      <td className={cx(styles['total-cell'], 'body-2')} data-test-basket-item-total>
        <p>{formatCurrency(itemTotal)}</p>
        <p className={cx(styles['quantity-subtext'], 'caption')}>
          {getQuantityHeader()}
          <Quantity quantity={quantity} unit={measureUnit} />
        </p>
        {quantity > 1 ? (
          <p className={cx(styles['unit-price-subtext'], 'caption')}>
            <FormattedMessage
              id="payment-link.itemisation.header.unit-price-each"
              values={{ unitPrice: formatCurrency(parseFloat(unitPrice.value)) }}
            />
          </p>
        ) : null}
      </td>
    </tr>
  );
}
